import SchemeChoiceFormAdapter from "./SchemeChoiceFormAdapter";

import "./Form.css";

import {
  sendViaPostMessage,
  focusPreviousInput,
  isExpiryDateValid,
  formatExpDate,
  validateCVV,
  formatCvv,
  sendToElementIframe,
} from "../../utils/";

import { hyphenCaseToCamelCase, removeSpaces } from "../../utils/string";

import {
  handleCustomStyleForm,
  handleCustomStyleAnimations,
} from "../../utils/stylesheet";

import {
  checkFormValidity,
  getNewValidationStatus,
  getCardData,
  getInputEmptyState,
  submitForm,
  isIOSDevice,
  fixIE11InputCursor,
  wrapOnInputForIE11,
  wrapOnInputForCompositionText,
  parseAndStyleEnvironment,
  setOnMouseOverClasses,
  setOnMouseOutClasses,
  onAutofillInput,
  setInputManualUseOnly,
  formatCard,
  getCardBIN,
  getCardScheme,
  isCardValid,
  isCvvValid,
  queryCardMetadataIfRequired,
  isElementEvent,
  isKeepingSchemeChoiceValue,
  getInitialConfigObjectMessageBuilder,
  setAriaInvalidAttribute,
  isCoBadgedCard,
} from "../shared";

import {
  ELEMENTS,
  EVENTS,
  VALIDATION_STATUSES,
  FRAMES_MODES,
  ELEMENT_FRAMES,
} from "../../constants";

import { isValid, isEmpty, hasValue } from "../../utils/validation";

import { forceBlurAllExcept, iOSFocusFix } from "../../utils/bugFixes";

import { createRisk } from "../../features/risk-js";
import {
  onBlur,
  onFocus,
  onFrameActivated,
  onValueChanged,
} from "../Controller/events";
import {
  buildCardImageBaseUrl,
  buildCardImageData,
} from "../buildCardImageUrl";
import { getSchemeChoiceOptionSchemeName } from "../SchemeChoice/schemeChoiceOption";

export const handleFocus = (element, input) => {
  input.classList.add(`${element}--focus`);

  sendViaPostMessage(EVENTS.FRAME_FOCUS, {
    element,
  });
};

export const handleBlur = (form, element, elementKey, input) => {
  input.classList.remove(`${element}--focus`);

  sendViaPostMessage(EVENTS.FRAME_BLUR, { element });

  if (
    form.state[elementKey].validationStatus !==
    form.previousState[elementKey].validationStatus
  ) {
    sendViaPostMessage(EVENTS.FRAME_VALIDATION_CHANGED, {
      element,
      isValid: isValid(form.state[elementKey].validationStatus),
      isEmpty: isEmpty(form.state[elementKey].validationStatus),
      isFormValid: isValid(form.state.validationStatus),
      isFormEmpty: isEmpty(form.state.validationStatus),
    });
  }
  setAriaInvalidAttribute(input, form.state[elementKey].validationStatus);
  handleValidationClasses(form, element, elementKey, input);
};

const handleValidationClasses = (form, element, elementKey, input) => {
  if (!isValid(form.state[elementKey].validationStatus)) {
    input.classList.remove(`${element}--valid`);
    input.classList.add(`${element}--invalid`);
  } else {
    input.classList.add(`${element}--valid`);
    input.classList.remove(`${element}--invalid`);
  }
};

class Form {
  state = {
    formSubmitting: false,
    loaded: false,
    hasLoadedInitialConfig: false,
    isSchemeChoiceReady: false,
    initialized: false,
    cvvOptional: false,
    cvvHidden: false,
    autoFocusDisabled: false,
    validationStatus: VALIDATION_STATUSES.INVALID,
    previousValidationStatus: VALIDATION_STATUSES.INVALID,
    cardNumber: getInputEmptyState(),
    expiryDate: getInputEmptyState(),
    cvv: getInputEmptyState(),
    config: { publicKey: "pk_test_8ac41c0d-fbcc-4ae3-a771-31ea533a2beb" },
    cardMetadata: {},
    schemeChoice: getInputEmptyState(),
  };

  previousState;
  paymentMethodLogo;
  cardNumberInput;
  expiryDateInput;
  cvvInput;
  schemeChoice;

  constructor(env) {
    this.env = parseAndStyleEnvironment(env);

    this.appUrl = this.env.APP_URL;
    this.isSchemeChoiceEnabled = this.env.isSchemeChoiceEnabled;

    if (this.env.isSchemeChoiceEnabled) {
      this.schemeChoice = new SchemeChoiceFormAdapter(this.env);
    }

    if (
      this.env.acceptedPaymentMethods &&
      this.env.acceptedPaymentMethods.length > 0
    ) {
      this.acceptedPaymentMethods = this.env.acceptedPaymentMethods;
    }

    this.state.cvvOptional = hasValue(
      this.env.MODES,
      FRAMES_MODES.CVV_OPTIONAL
    );
    this.state.cvvHidden = hasValue(this.env.MODES, FRAMES_MODES.CVV_HIDDEN);
    this.state.autoFocusDisabled = hasValue(
      this.env.MODES,
      FRAMES_MODES.DISABLE_AUTO_FOCUS
    );
    const copyPasteDisabled = hasValue(
      this.env.MODES,
      FRAMES_MODES.DISABLE_COPY_PASTE
    );
    const rightToLeftEnabled = hasValue(
      this.env.MODES,
      FRAMES_MODES.RIGHT_TO_LEFT
    );

    this.imageUrl = buildCardImageBaseUrl(this.appUrl);

    // deep copying - Object.assign (or similar) will cause issues as this.state has object references
    // (cardNumber, cvv, expiryDate)
    this.previousState = JSON.parse(JSON.stringify(this.state));

    this.paymentMethodLogo = document.getElementById("payment-method-logo");
    this.paymentMethodLogo.src = buildCardImageData(this.imageUrl).src;
    this.cardNumberInput = document.getElementById(
      "checkout-frames-card-number"
    );
    this.expiryDateInput = document.getElementById(
      "checkout-frames-expiry-date"
    );
    this.cvvInput = document.getElementById("checkout-frames-cvv");

    // card number event handlers
    this.cardNumberInput.addEventListener(
      "input",
      wrapOnInputForIE11(
        wrapOnInputForCompositionText(this.onInputChangeCardNumber)
      )
    );
    this.cardNumberInput.addEventListener(
      "compositionend",
      this.onInputChangeCardNumber
    );
    this.cardNumberInput.addEventListener(
      "mouseover",
      this.onMouseOverCardNumber
    );
    this.cardNumberInput.addEventListener(
      "mouseout",
      this.onMouseOutCardNumber
    );
    this.cardNumberInput.addEventListener("focus", this.onFocusCardNumber);
    this.cardNumberInput.addEventListener("blur", this.onBlurCardNumber);

    // expiry date event handlers
    this.expiryDateInput.addEventListener(
      "input",
      wrapOnInputForIE11(
        wrapOnInputForCompositionText(this.onInputChangeExpiryDate)
      )
    );
    this.expiryDateInput.addEventListener(
      "compositionend",
      this.onInputChangeExpiryDate
    );
    this.expiryDateInput.addEventListener(
      "mouseover",
      this.onMouseOverExpiryDate
    );
    this.expiryDateInput.addEventListener(
      "mouseout",
      this.onMouseOutExpiryDate
    );
    this.expiryDateInput.addEventListener("focus", this.onFocusExpiryDate);
    this.expiryDateInput.addEventListener("blur", this.onBlurExpiryDate);
    this.expiryDateInput.addEventListener(
      "keydown",
      focusPreviousInput(this.cardNumberInput)
    );

    if (this.state.cvvHidden) {
      // IE compatible removing elements from DOM
      this.cvvInput.parentNode.removeChild(this.cvvInput);
    } else {
      // cvv event handlers
      this.cvvInput.addEventListener(
        "input",
        wrapOnInputForIE11(wrapOnInputForCompositionText(this.onInputChangeCvv))
      );
      this.cvvInput.addEventListener("compositionend", this.onInputChangeCvv);
      this.cvvInput.addEventListener("mouseover", this.onMouseOverCvv);
      this.cvvInput.addEventListener("mouseout", this.onMouseOutCvv);
      this.cvvInput.addEventListener("focus", this.onFocusCvv);
      this.cvvInput.addEventListener("blur", this.onBlurCvv);
      this.cvvInput.addEventListener(
        "keydown",
        focusPreviousInput(this.expiryDateInput)
      );
    }

    window.addEventListener("message", (e) => {
      isElementEvent(e)
        ? this.onElementMessage(e)
        : this.onCrossOriginMessageReceived(e);
    });

    if (copyPasteDisabled) {
      [
        this.cardNumberInput,
        this.expiryDateInput,
        this.cvvInput,
      ].forEach((el) => setInputManualUseOnly(el));
    }

    if (rightToLeftEnabled) {
      this.cardNumberInput.parentNode.setAttribute("dir", "rtl");
    }

    if (isIOSDevice()) {
      iOSFocusFix(() => {});
    }

    this.initialize();
  }

  onElementMessage = (e) => {
    const { element, data } = e.data;
    const { state } = this;
    const { FRAME_ACTIVATED, FRAME_FOCUS, FRAME_BLUR, VALUE_CHANGED } = EVENTS;

    switch (data.event) {
      case FRAME_ACTIVATED:
        if (element === ELEMENTS.SCHEME_CHOICE) {
          this.state.isSchemeChoiceReady = true;
          this.sendInitialConfigToSchemeChoiceIfDataPresent();
        }
        onFrameActivated(state, element);
        break;
      case FRAME_FOCUS:
        if (isIOSDevice()) {
          forceBlurAllExcept(hyphenCaseToCamelCase(element));
        }
        onFocus(state, element, data);
        break;
      case FRAME_BLUR:
        onBlur(state, element, data);
        break;
      case VALUE_CHANGED:
        onValueChanged(state, element, data);
        if (element === ELEMENTS.SCHEME_CHOICE) {
          this.onSchemeChoiceSelectionChange();
        }
        break;
    }

    checkFormValidity(this);
  };

  onSchemeChoiceSelectionChange = () => {
    const currentScheme =
      this.state.schemeChoice.value ||
      this.state.cardMetadata.schemes?.scheme ||
      this.state.cardNumber.scheme;

    this.setImageOnSchemeChange(currentScheme);
    this.emitCardBinChanged(currentScheme);
  };

  get cardData() {
    return getCardData.call(this);
  }

  /**
   * Card number change handler
   */
  onInputChangeCardNumber = (event) => {
    const changeValue = event.target.value;
    const formattedValue = formatCard(changeValue);
    const queryCardMetadataCurrentStatus = this.queryCardMetadata(changeValue);

    this.state.cardNumber.value = removeSpaces(formattedValue);
    this.state.cardNumber.formattedValue = formattedValue;
    this.state.cardNumber.scheme = getCardScheme(this.state.cardNumber.value);
    this.state.cardNumber.bin = getCardBIN(this.state.cardNumber.value);

    this.cardNumberInput.value = formattedValue;

    const hasSchemeValueChanged =
      this.state.cardNumber.scheme !== this.previousState.cardNumber.scheme;

    const isSelectingNewScheme = isKeepingSchemeChoiceValue(
      queryCardMetadataCurrentStatus
    );

    if (hasSchemeValueChanged) {
      this.setImageOnSchemeChange();

      const oldScheme = this.previousState.cardNumber.scheme;
      const newScheme = this.state.cardNumber.scheme;
      this.previousState.cardNumber.scheme = newScheme;
      validateCVV(oldScheme, newScheme, this.clearCVV);

      const scheme =
        this.state.schemeChoice.value ||
        this.state.cardMetadata.schemes?.scheme;

      if (isSelectingNewScheme) {
        this.setImageOnSchemeChange(scheme);
      }
    }

    if (
      this.state.cardNumber.bin &&
      this.state.cardNumber.bin !== this.previousState.cardNumber.bin
    ) {
      this.previousState.cardNumber.bin = this.state.cardNumber.bin;
      this.emitCardBinChanged();
    }

    this.setCardNumberValidationStatus();
    checkFormValidity(this);

    onAutofillInput(event);

    fixIE11InputCursor(this.cardNumberInput);
  };

  queryCardMetadata = (changeValue) => {
    if (this.isSchemeChoiceEnabled) {
      return queryCardMetadataIfRequired({
        changedValue: changeValue,
        appState: this.state,
        appUrl: this.appUrl,
        onValueChange: this.schemeChoice?.toggleElementVisibility,
      });
    }
  };

  emitCardBinChanged = (providedScheme) => {
    const scheme = this.getSchemeName(providedScheme);
    const isCoBadged = isCoBadgedCard(this.state.cardMetadata.schemes);

    sendViaPostMessage(EVENTS.CARD_BIN_CHANGED, {
      bin: this.state.cardNumber.bin,
      isCoBadged,
      scheme,
    });
  };

  /**
   * Expiry Date change handler
   */
  onInputChangeExpiryDate = (event) => {
    const oldValue = this.state.expiryDate.value;
    const newValue = event.target.value;

    const formattedValue = formatExpDate(oldValue, newValue);

    this.state.expiryDate.value = formattedValue;
    this.expiryDateInput.value = formattedValue;

    if (
      !this.state.autoFocusDisabled &&
      !this.state.cvvHidden &&
      this.expiryDateInput.value.length === 5
    ) {
      this.cvvInput.focus();
    }

    this.setExpiryDateValidationStatus();
    checkFormValidity(this);

    onAutofillInput(event);

    fixIE11InputCursor(this.expiryDateInput);
  };

  /**
   * Cvv change handler
   */
  onInputChangeCvv = (event) => {
    const currentScheme = this.state.cardNumber.scheme;
    const formattedValue = formatCvv(event.target.value, currentScheme);

    this.state.cvv.value = formattedValue;
    this.cvvInput.value = formattedValue;

    this.setCvvValidationStatus();
    checkFormValidity(this);

    onAutofillInput(event);

    fixIE11InputCursor(this.cvvInput);
  };

  clearCVV = () => {
    this.cvvInput.value = "";
    this.state.cvv.value = "";
  };

  setImageOnSchemeChange = (providedScheme) => {
    const scheme = this.getSchemeName(providedScheme);
    const { alt, src } = buildCardImageData(this.imageUrl, scheme);
    const classes = ["payment-method-logo"];
    if (!scheme) {
      classes.push("payment-method-logo-alignment");
    }

    this.paymentMethodLogo.setAttribute("src", src);
    this.paymentMethodLogo.setAttribute("alt", alt);
    this.paymentMethodLogo.setAttribute("class", classes.join(" "));

    sendViaPostMessage(EVENTS.PAYMENT_METHOD_CHANGED, {
      isValid: this.state.validationStatus === VALIDATION_STATUSES.VALID,
      paymentMethod: scheme,
      isPaymentMethodAccepted:
        !this.acceptedPaymentMethods ||
        this.acceptedPaymentMethods.indexOf(scheme) >= 0,
    });
  };

  getSchemeName = (providedScheme) => {
    return (
      getSchemeChoiceOptionSchemeName(providedScheme) ||
      this.state.cardNumber.scheme
    );
  };

  onMouseOverCardNumber = () => {
    setOnMouseOverClasses(this.cardNumberInput, "card-number");
  };

  onMouseOutCardNumber = () => {
    setOnMouseOutClasses(this.cardNumberInput, "card-number");
  };

  onFocusCardNumber = () => {
    this.setCardNumberValidationStatus();

    handleFocus(ELEMENTS.CARD_NUMBER, this.cardNumberInput);
  };

  onBlurCardNumber = () => {
    this.setCardNumberValidationStatus();

    handleBlur(this, ELEMENTS.CARD_NUMBER, "cardNumber", this.cardNumberInput);

    this.previousState.cardNumber.validationStatus = this.state.cardNumber.validationStatus;
  };

  setCardNumberValidationStatus = () => {
    this.state.cardNumber.validationStatus = getNewValidationStatus(
      this.cardNumberInput.value,
      isCardValid,
      this.state.cardNumber.value,
      this.state.cardNumber.scheme
    );
  };

  setExpiryDateValidationStatus = () => {
    this.state.expiryDate.validationStatus = getNewValidationStatus(
      this.expiryDateInput.value,
      isExpiryDateValid,
      this.state.expiryDate.value
    );
  };

  onMouseOverExpiryDate = () => {
    setOnMouseOverClasses(this.expiryDateInput, "expiry-date");
  };

  onMouseOutExpiryDate = () => {
    setOnMouseOutClasses(this.expiryDateInput, "expiry-date");
  };

  onFocusExpiryDate = () => {
    this.setExpiryDateValidationStatus();

    handleFocus(ELEMENTS.EXPIRY_DATE, this.expiryDateInput);
  };

  onBlurExpiryDate = () => {
    this.setExpiryDateValidationStatus();

    handleBlur(this, ELEMENTS.EXPIRY_DATE, "expiryDate", this.expiryDateInput);

    this.previousState.expiryDate.validationStatus = this.state.expiryDate.validationStatus;
  };

  setCvvValidationStatus = () => {
    this.state.cvv.validationStatus = getNewValidationStatus(
      this.cvvInput.value,
      isCvvValid,
      this.state.cvv.value,
      this.state.cardNumber.scheme
    );
  };

  onMouseOverCvv = () => {
    setOnMouseOverClasses(this.cvvInput, "cvv");
  };

  onMouseOutCvv = () => {
    setOnMouseOutClasses(this.cvvInput, "cvv");
  };

  onFocusCvv = () => {
    this.setCvvValidationStatus();

    handleFocus(ELEMENTS.CVV, this.cvvInput);
  };

  onBlurCvv = () => {
    this.setCvvValidationStatus();

    handleBlur(this, ELEMENTS.CVV, "cvv", this.cvvInput);

    this.previousState.cvv.validationStatus = this.state.cvv.validationStatus;
  };

  onCrossOriginMessageReceived = (e) => {
    const { message } = e.data;
    // react to the messages sent by the merchant pages
    switch (message) {
      case "initial-config":
        this.onReceiveInitialConfig(e.data.data);
        break;
      case "submit-form": {
        const data = {
          cardholder: e.data.args.cardholder,
        };
        submitForm.call(this, data, this.appUrl, this.onCardTokenized);
        break;
      }
      case "enable-submit-form":
        this.state.formSubmitting = false;
        break;
      default:
        break;
    }
  };

  onReceiveInitialConfig = (config) => {
    const { style, placeholder } = config;

    this.state = {
      ...this.state,
      hasLoadedInitialConfig: true,
      config,
    };

    this.sendInitialConfigToSchemeChoiceIfDataPresent();

    if (style) {
      handleCustomStyleForm(style, this.env.isSchemeChoiceEnabled);
      if (style.animation) {
        handleCustomStyleAnimations(style.animation);
      }
    }
    if (placeholder) {
      const { cardNumber, expiryDate, cvv } = placeholder;
      if (cardNumber) this.cardNumberInput.placeholder = cardNumber;
      if (expiryDate) this.expiryDateInput.placeholder = expiryDate;
      if (cvv) this.cvvInput.placeholder = cvv;
    }

    createRisk(this.env, this.appUrl, this.state.config.publicKey)
      .then((risk) => {
        this.risk = risk;
      })
      .catch(() => {
        this.risk = null;
      });
  };

  sendInitialConfigToSchemeChoiceIfDataPresent = () => {
    const {
      isSchemeChoiceEnabled,
      state: { hasLoadedInitialConfig, isSchemeChoiceReady, config },
    } = this;

    if (
      hasLoadedInitialConfig &&
      isSchemeChoiceEnabled &&
      isSchemeChoiceReady
    ) {
      const messageBuilder = getInitialConfigObjectMessageBuilder(config);
      const field = ELEMENT_FRAMES.SCHEME_CHOICE;
      sendToElementIframe(field, messageBuilder(field));
    }
  };

  onCardTokenized = async (token) => {
    if (this.risk) {
      await this.risk.publishData(token);
    }
  };

  initialize() {
    this.state.initialized = true;

    sendViaPostMessage(EVENTS.READY, {});
  }
}

export default Form;
